// web/src/billing/PlanCard.tsx
//
// One plan card on /pricing. Renders the dynamic CTA per the matrix in
// Spec §4 (signed-in × tier × current plan). The parent (PricingPage)
// owns the actual checkout / portal calls; this component just emits
// onStartTrial / onSwitchPlan with the plan + cadence selected.
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check } from "lucide-react";

export type CardTier = "free" | "pro" | "analyst";
export type CardCadence = "monthly" | "annual";

export type ViewerState =
  | { kind: "signed_out" }
  | {
      kind: "signed_in";
      currentTier: "free" | "pro" | "analyst";
      currentCadence: CardCadence | null;
    };

interface Props {
  tier: CardTier;
  cadence: CardCadence;
  price: string;
  bullets: string[];
  viewerState: ViewerState;
  onStartTrial: (args: { plan: "pro" | "analyst"; cadence: CardCadence }) => void;
  onSwitchPlan: () => void;
}

export function PlanCard({
  tier,
  cadence,
  price,
  bullets,
  viewerState,
  onStartTrial,
  onSwitchPlan,
}: Props) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>{tier.charAt(0).toUpperCase() + tier.slice(1)}</CardTitle>
        <div className="text-3xl font-semibold">{price}</div>
        <div className="text-sm text-muted-foreground">
          {tier === "free" ? "forever" : cadence === "monthly" ? "per month" : "per year"}
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-3">
        <ul className="flex-1 space-y-2 text-sm">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <Check className="size-4 mt-0.5 shrink-0 text-emerald-600" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <CardCta
          tier={tier}
          cadence={cadence}
          viewerState={viewerState}
          onStartTrial={onStartTrial}
          onSwitchPlan={onSwitchPlan}
        />
      </CardContent>
    </Card>
  );
}


function CardCta({
  tier, cadence, viewerState, onStartTrial, onSwitchPlan,
}: Pick<Props, "tier" | "cadence" | "viewerState" | "onStartTrial" | "onSwitchPlan">) {
  // Free card behaviour
  if (tier === "free") {
    if (viewerState.kind === "signed_out") {
      return (
        <Button asChild variant="outline">
          <Link to="/auth/signup">Sign up</Link>
        </Button>
      );
    }
    if (viewerState.currentTier === "free") {
      return <Button disabled variant="outline">You're on Free</Button>;
    }
    return <Button variant="outline" onClick={onSwitchPlan}>Downgrade</Button>;
  }

  // Pro / Analyst CTAs
  if (viewerState.kind === "signed_out") {
    return (
      <Button asChild>
        <Link to={`/auth/signup?plan=${tier}&cadence=${cadence}`}>
          Start free 14-day trial
        </Link>
      </Button>
    );
  }

  const isCurrent =
    viewerState.currentTier === tier && viewerState.currentCadence === cadence;
  if (isCurrent) {
    return <Button disabled>Current plan</Button>;
  }

  if (viewerState.currentTier === "free") {
    return (
      <Button onClick={() => onStartTrial({ plan: tier, cadence })}>
        Start free 14-day trial
      </Button>
    );
  }

  return <Button onClick={onSwitchPlan}>Switch plan</Button>;
}
